<template>
  <div class="proposal-cardlet" @click="navigateToProposal">
    <div class="cardlet-info">
      <div class="day-time">
        <span class="day" :class="{ 'is-highlight': isRelativeDay }">{{
          isRelativeDay ? relativeDayLabel : dayLabel
        }}</span>
        <span>&middot;</span>
        <span class="time">{{ startTimeLabel }}</span>
      </div>

      <div v-if="gambitTags.length" class="cardlet-tags">
        <GamChip v-for="(tag, index) in gambitTags" v-bind="tag" :key="`tag-${index}`" is-upper-case />
      </div>

      <div class="cardlet-status">
        <template v-if="localIsSuccessful">
          <span class="status-confirmed"><GamIcon :name="GamIconName.CHECK" size="10px" is-current /> Confirmed</span>
        </template>
        <template v-else-if="playersNeeded > 0">
          <span class="status-needed">
            <span class="dot" />
            {{ localConfirmations.length === 0 ? `${playersNeeded} needed` : `${playersNeeded} more needed` }}
          </span>
        </template>
      </div>
    </div>

    <div class="cardlet-cta" @click.stop>
      <template v-if="localIsSuccessful">
        <GamButton
          class="cta-circle"
          :variant="GamButtonVariant.SECONDARY"
          :size="GamButtonSize.LARGE"
          :left-icon="GamIconName.SMALL_ARROW_RIGHT"
          is-icon-only
          @click.stop="handleOpenGambit"
        />
      </template>

      <template v-else-if="isUserConfirmed">
        <GamButton
          v-if="showMeetAnyways"
          :class="['cta-circle', 'play-cta', 'play-cta-proposal', { 'is-inverted': localUserHasMeetAnyways }]"
          :variant="GamButtonVariant.SLAB"
          :size="GamButtonSize.LARGE"
          :left-icon="GamIconName.FAST"
          :is-disabled="isSubmitting"
          :is-loading="isSubmitting"
          is-icon-only
          @click.stop="handleMeetAnyways"
        />

        <GamButton
          class="cta-circle play-cta play-cta-proposal is-inverted"
          :variant="GamButtonVariant.SLAB"
          :size="GamButtonSize.LARGE"
          :left-icon="GamIconName.PAUSE"
          :is-disabled="isSubmitting"
          :is-loading="isSubmitting"
          is-icon-only
          @click.stop="handleLeaveProposal"
        />
      </template>

      <template v-else>
        <GamButton
          class="cta-circle play-cta play-cta-proposal"
          :variant="GamButtonVariant.SLAB"
          :size="GamButtonSize.LARGE"
          :left-icon="GamIconName.PLAY_EMPTY"
          :is-disabled="isSubmitting"
          :is-loading="isSubmitting"
          is-icon-only
          @click.stop="handleJoinProposal"
        />
      </template>
    </div>
  </div>
</template>

<script lang="ts">
export interface GambitTearOffType {
  gambit: NextGambitDto;
  timezone?: string | null;
  isRounded?: boolean;
  isPaddingReduced?: boolean;
  showAttendees?: boolean;
  isInvited?: boolean;
  intentId?: string | null;
}
</script>

<script setup lang="ts">
import { RouterNameType } from '@/core/data/config/uiConfig.interface';
import type { BaseGambitDto, GambitProposalConfirmationSummaryDto } from '@/core/data/gambit/gambit.interface';
import type { NextGambitDto } from '@/core/data/location/location.interface';
import { gambits, localize } from '@/core/gambits';
import GambitHelper from '@/core/helpers/gambit.helper';
import PlayerHelper from '@/core/helpers/player.helper';
import { useAuthStore } from '@/stores/auth.store';
import GamButton from '@/views/components/GamButton.vue';
import GamChip from '@/views/components/GamChip.vue';
import GamIcon from '@/views/components/GamIcon.vue';
import { GamButtonSize, GamButtonVariant } from '@/views/composables/constants/components/gamButton.constants';
import { GamChipSize, GamChipVariant } from '@/views/composables/constants/components/gamChip.constants';
import { GamDateType } from '@/views/composables/constants/components/gamGambit.constants';
import { GamIconName } from '@/views/composables/constants/components/gamIcon.constants';
import { useUnpairedChip } from '@/views/composables/hooks/useUnpairedChip';
import { formatInTimeZone } from 'date-fns-tz';
import { storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';
import { useRouter } from 'vue-router';

const props = defineProps<GambitTearOffType>();
const emit = defineEmits<{ 'action-completed': [] }>();

const resolvedTimezone = computed(() => props.timezone || 'UTC');

const startTimeLabel = computed(() => formatInTimeZone(props.gambit.start, resolvedTimezone.value, 'HH:mm'));
const dayLabel = computed(() => formatInTimeZone(props.gambit.start, resolvedTimezone.value, 'EEEE'));

const dateType = computed(() => GambitHelper.getGambitsDateType(props.gambit.start, false, props.gambit.end));

const isRelativeDay = computed(() => dateType.value !== GamDateType.DAY);
const relativeDayLabel = computed(() => localize(`gambit.card.date.time.type.${dateType.value}`));

const authStore = useAuthStore();
const { userDetails } = storeToRefs(authStore);

const router = useRouter();

const isSubmitting = ref(false);

const localConfirmations = ref<GambitProposalConfirmationSummaryDto[]>([
  ...(props.gambit.proposal?.confirmations || []),
]);
const localIsSuccessful = ref(props.gambit.proposal?.isSuccessful || false);

const deriveUserMeetAnyways = (confirmations: GambitProposalConfirmationSummaryDto[]) => {
  const uid = userDetails.value?.id;
  if (!uid) return false;
  return !!confirmations.find((c) => c.userId === uid)?.meetAnywaysAt;
};

const localUserHasMeetAnyways = ref(deriveUserMeetAnyways(props.gambit.proposal?.confirmations || []));

/* Status stays a line. A proposal's players live on its confirmations, so that
   chip and the player's rating sit with the type tags, same as a spark card.
   The elo range stays only when nobody confirmed has a rating yet. */
const confirmationRatingTag = computed(() => {
  const presentation = PlayerHelper.groupRatingPresentation(localConfirmations.value);
  if (!presentation) return null;

  return {
    ...presentation,
    size: GamChipSize.X_SMALL,
    leftIcon: GamIconName.GAMBIT,
  };
});

const unpairedChip = useUnpairedChip(() => localConfirmations.value.map((confirmation) => confirmation.userId));

const gambitTags = computed(() => {
  const proposal = props.gambit.proposal
    ? { ...props.gambit.proposal, confirmations: localConfirmations.value }
    : props.gambit.proposal;
  let tags = GambitHelper.getGambitsTags({ ...props.gambit, proposal } as unknown as BaseGambitDto, {
    size: GamChipSize.X_SMALL,
    showAttendees: false,
    showConfirmations: true,
    showRating: !confirmationRatingTag.value,
    showChessSets: false,
    highlightAttendance: true,
  }).map((tag) => (tag.variant === GamChipVariant.PRIMARY ? { ...tag, isActive: true } : tag));

  const rating = confirmationRatingTag.value;
  if (rating) {
    const [first, ...rest] = tags;
    tags = first?.leftIcon === GamIconName.PAWN ? [first, rating, ...rest] : [rating, ...tags];
  }

  return unpairedChip.value ? [unpairedChip.value, ...tags] : tags;
});

watch(
  () => props.gambit.proposal,
  (next) => {
    localConfirmations.value = [...(next?.confirmations || [])];
    localIsSuccessful.value = next?.isSuccessful || false;
    localUserHasMeetAnyways.value = deriveUserMeetAnyways(next?.confirmations || []);
  },
);

const proposal = computed(() => props.gambit.proposal);

const isUserConfirmed = computed(() => {
  if (!userDetails.value?.id) return false;
  return localConfirmations.value.some((c) => c.userId === userDetails.value!.id);
});

const playersNeeded = computed(() => {
  return Math.max(0, 4 - localConfirmations.value.length);
});

const showMeetAnyways = computed(() => {
  return isUserConfirmed.value && !localIsSuccessful.value;
});

const doNavigateToProposal = () => {
  if (!props.gambit) return;

  router.push({
    name: RouterNameType.SPARKS,
    query: {
      date: formatInTimeZone(props.gambit.start, resolvedTimezone.value, 'yyyy-MM-dd'),
      view: props.gambit.proposal?.id ? `proposal:${props.gambit.proposal.id}` : undefined,
      from: 'card',
    },
  });
};

const navigateToProposal = () => {
  if (!props.gambit) return;
  doNavigateToProposal();
};

const handleJoinProposal = async () => {
  if (!proposal.value?.id || !userDetails.value?.id) return;

  const intentId = props.intentId;
  if (!intentId) {
    navigateToProposal();
    return;
  }

  isSubmitting.value = true;

  localConfirmations.value = [
    ...localConfirmations.value,
    {
      id: crypto.randomUUID(),
      userId: userDetails.value.id,
      username: userDetails.value.username,
      rating: userDetails.value.rating,
      profileImageBlobHash: userDetails.value.profileImageBlobHash ?? undefined,
    },
  ];

  const result = await gambits.sparkService.createConfirmation(proposal.value.id, intentId);

  if (result.data) {
    localIsSuccessful.value = !!result.data.isSuccessful;
    if (result.data.isSuccessful && result.data.gambitId) {
      router.push({ name: RouterNameType.GAMBIT, params: { id: result.data.gambitId }, query: { back: 'history' } });
    }
  } else {
    localConfirmations.value = localConfirmations.value.filter((c) => c.userId !== userDetails.value!.id);
  }

  isSubmitting.value = false;
  emit('action-completed');
};

const handleLeaveProposal = async () => {
  if (!proposal.value?.id || !userDetails.value?.id) return;

  isSubmitting.value = true;

  const prevConfirmations = [...localConfirmations.value];
  localConfirmations.value = localConfirmations.value.filter((c) => c.userId !== userDetails.value!.id);
  localUserHasMeetAnyways.value = false;

  const result = await gambits.sparkService.deleteConfirmation(proposal.value.id);

  if (result.error) {
    localConfirmations.value = prevConfirmations;
  }

  isSubmitting.value = false;
  emit('action-completed');
};

const handleMeetAnyways = async () => {
  if (!proposal.value?.id) return;

  isSubmitting.value = true;

  if (localUserHasMeetAnyways.value) {
    localUserHasMeetAnyways.value = false;
    const result = await gambits.sparkService.undoMeetAnyways(proposal.value.id);
    if (!result.data) {
      localUserHasMeetAnyways.value = true;
    } else {
      localIsSuccessful.value = !!result.data.isSuccessful;
    }
  } else {
    localUserHasMeetAnyways.value = true;
    const result = await gambits.sparkService.meetAnyways(proposal.value.id);
    if (!result.data) {
      localUserHasMeetAnyways.value = false;
    } else {
      localIsSuccessful.value = !!result.data.isSuccessful;
      if (result.data.isSuccessful && result.data.gambitId) {
        router.push({ name: RouterNameType.GAMBIT, params: { id: result.data.gambitId }, query: { back: 'history' } });
      }
    }
  }

  isSubmitting.value = false;
  emit('action-completed');
};

const handleOpenGambit = () => {
  const gambitId = props.gambit.id;
  if (!gambitId) return;

  router.push({ name: RouterNameType.GAMBIT, params: { id: gambitId }, query: { back: 'history' } });
};
</script>

<style lang="scss" scoped>
@use '@/ui/css/mixins' as *;

.proposal-cardlet {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 0.75rem 0;
  gap: 1rem;
  cursor: pointer;
}

.cardlet-info {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
  flex: 1;
}

.day-time {
  display: flex;
  align-items: center;
  gap: 0.3em;
  font-size: 16px;
  letter-spacing: 0.05em;
  white-space: nowrap;
}

.day,
.time {
  text-transform: uppercase;
  font-weight: 400;
  color: var(--color-white);
}

.day.is-highlight {
  color: var(--color-pink);
}

/* Scrolls inside the info column so the CTAs keep their own width. */
.cardlet-tags {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: var(--spacing-xs);
  width: 100%;
  min-width: 0;
  overflow-x: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  > * {
    flex-shrink: 0;
  }
}

.cardlet-status {
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  font-weight: 600;
}

.status-confirmed {
  color: var(--color-green);
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;

  :deep(svg path) {
    stroke: currentColor;
    stroke-width: 1px;
  }
}

.status-needed {
  color: var(--color-orange);
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;

  .dot {
    width: 6px;
    height: 6px;
    min-width: 6px;
    border-radius: 50%;
    background-color: currentColor;
  }
}

.cardlet-cta {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: default;
}

.cta-circle :deep(.gam-button-container) {
  border-radius: 50% !important;
  width: 44px !important;
  height: 44px !important;
  min-width: 44px !important;
  padding: 0 !important;

  .icon {
    position: absolute !important;
    left: 50% !important;
    top: 50% !important;
    transform: translate(-50%, -50%) !important;
  }

  .svg-container {
    font-size: 22px !important;
    width: 22px !important;
    height: 22px !important;
  }
}

@include play-cta;
</style>
